
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import Header from '@/components/Header.jsx';
import Footer from '@/components/Footer.jsx';
import { motion } from 'framer-motion';
import Seo from '@/components/Seo.jsx';

function CookiePolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Seo
        title="Politique de Cookies | Neuvième Continent"
        description="Informations détaillées sur l'utilisation des cookies sur le site de l'association Neuvième Continent."
      />

      <Header />

      <main className="flex-grow py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-12 policy-content"
          >
            <div className="relative flex flex-col items-center justify-center mb-16">
              <button 
                onClick={() => navigate('/')}
                className="absolute right-0 top-0 md:-top-2 p-3 rounded-full bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-sm hover:shadow-md hover:scale-110 z-10"
                aria-label="Retour à l'accueil"
              >
                <X className="w-6 h-6 md:w-7 md:h-7" />
              </button>
              
              <div className="text-center space-y-4 pr-16 md:pr-0 w-full">
                <h1 className="text-4xl md:text-5xl font-bold font-elegant text-foreground tracking-tight">
                  Politique de Cookies
                </h1>
                <p className="text-muted-foreground">Dernière mise à jour : 24 Mars 2026</p>
              </div>
            </div>

            <section>
              <h2>1. Introduction</h2>
              <p>
                Cette politique explique ce que sont les cookies, comment l'association <strong>Neuvième Continent</strong> les utilise sur son site web, et comment vous pouvez les gérer. Un cookie est un petit fichier texte enregistré sur votre terminal (ordinateur, tablette, smartphone) lors de la visite d'un site web. Il permet au site de mémoriser vos actions et préférences pendant une durée donnée.
              </p>
            </section>

            <section>
              <h2>2. Types de Cookies Utilisés</h2>
              <p>Nous utilisons différents types de cookies pour assurer le bon fonctionnement de notre site et améliorer votre expérience :</p>
              <ul>
                <li><strong>Cookies Essentiels :</strong> Ces cookies sont strictement nécessaires au fonctionnement du site (gestion de session, sécurité). Ils ne peuvent pas être désactivés dans nos systèmes.</li>
                <li><strong>Cookies Analytiques :</strong> Ils nous permettent de mesurer l'audience de notre site, de savoir quelles pages sont les plus populaires et de comprendre comment les visiteurs naviguent, afin d'améliorer nos contenus.</li>
                <li><strong>Cookies Fonctionnels :</strong> Ils permettent au site de mémoriser vos choix (comme votre langue ou vos préférences d'affichage) pour vous proposer une expérience personnalisée.</li>
              </ul>
            </section>

            <section>
              <h2>3. Gestion de votre Consentement</h2>
              <p>
                Lors de votre première visite sur notre site, un bandeau vous informe de la présence de cookies et vous invite à indiquer votre choix. Vous pouvez accepter tous les cookies, les refuser (à l'exception des cookies essentiels), ou personnaliser vos préférences.
              </p>
              <div className="mt-6">
                <button 
                  onClick={() => window.dispatchEvent(new CustomEvent('open-cookie-settings'))}
                  className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-xl transition-colors shadow-md"
                >
                  Gérer mes préférences de cookies
                </button>
              </div>
            </section>

            <section>
              <h2>4. Gérer les Cookies via votre Navigateur</h2>
              <p>
                Vous pouvez également configurer votre navigateur pour qu'il refuse tous les cookies ou qu'il vous signale quand un cookie est envoyé. Voici comment procéder pour les principaux navigateurs :
              </p>
              <ul>
                <li><strong>Google Chrome :</strong> Paramètres &gt; Confidentialité et sécurité &gt; Cookies et autres données des sites.</li>
                <li><strong>Mozilla Firefox :</strong> Options &gt; Vie privée et sécurité &gt; Cookies et données de sites.</li>
                <li><strong>Apple Safari :</strong> Préférences &gt; Confidentialité &gt; Bloquer tous les cookies.</li>
                <li><strong>Microsoft Edge :</strong> Paramètres &gt; Cookies et autorisations de site &gt; Gérer et supprimer les cookies.</li>
              </ul>
              <p>
                Pour en savoir plus sur la gestion des cookies, vous pouvez visiter le site <a href="https://www.allaboutcookies.org/fr/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.allaboutcookies.org</a>.
              </p>
            </section>

            <section>
              <h2>5. Cookies Tiers</h2>
              <p>
                Certaines de nos pages affichent des contenus de fournisseurs externes (comme des vidéos YouTube ou des cartes interactives). Pour visualiser ces contenus tiers, vous devez d'abord accepter leurs conditions particulières, qui incluent leurs politiques en matière de cookies sur lesquelles nous n'avons aucun contrôle.
              </p>
            </section>

            <section>
              <h2>6. Tableau Détaillé des Cookies</h2>
              <div className="overflow-x-auto mt-6">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr>
                      <th>Nom du Cookie</th>
                      <th>Finalité</th>
                      <th>Type</th>
                      <th>Durée</th>
                      <th>Fournisseur</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><code>cookieConsent</code></td>
                      <td>Mémorise vos préférences de consentement aux cookies.</td>
                      <td>Essentiel</td>
                      <td>1 an</td>
                      <td>Neuvième Continent</td>
                    </tr>
                    <tr>
                      <td><code>_ga</code></td>
                      <td>Utilisé pour distinguer les utilisateurs (Google Analytics).</td>
                      <td>Analytique</td>
                      <td>2 ans</td>
                      <td>Google</td>
                    </tr>
                    <tr>
                      <td><code>_gid</code></td>
                      <td>Utilisé pour distinguer les utilisateurs (Google Analytics).</td>
                      <td>Analytique</td>
                      <td>24 heures</td>
                      <td>Google</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default CookiePolicyPage;
